<script setup>
import { displayGameName } from '../../../shared/game-display-name.mjs'
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import AppBottomNav from '../components/AppBottomNav.vue'
import NoticeModal from '../components/NoticeModal.vue'
import LegacyAlert from '../components/LegacyAlert.vue'

const games = ref([])
const gameTypes = ref({})
const notices = ref([])
const profile = ref(null)
const drawer = ref(false)
const themeOpen = ref(false)
const displayMode = ref('default')
const theme = ref(localStorage.getItem('lottery-theme') || 'red')
const failed = ref(new Set())
const swipeStartX = ref(null)
const newest = computed(() => { const list = games.value.filter((game) => game.game_attribute?.includes('n')); return (list.length ? list : games.value).slice(0, 8) })
const hot = computed(() => { const list = games.value.filter((game) => game.game_attribute?.includes('h')); return (list.length ? list : games.value).slice(0, 8) })
function gameTypeName(game) { return game.game_type || game.type_name || gameTypes.value[game.game_template] || game.game_template || '' }
const gameSections = computed(() => {
  if (displayMode.value === 'default') return [{ title: '新上游戏', games: newest.value }, { title: '热门游戏', games: hot.value }]
  const groups = new Map()
  games.value.forEach((game) => { const key = game.game_template || game.game_type || 'other'; if (!groups.has(key)) groups.set(key, []); groups.get(key).push(game) })
  const order = ['赛车', '牌九', '番摊', '大吃小']
  return [...groups].sort(([a], [b]) => (order.indexOf(gameTypes.value[a] || a) < 0 ? 99 : order.indexOf(gameTypes.value[a] || a)) - (order.indexOf(gameTypes.value[b] || b) < 0 ? 99 : order.indexOf(gameTypes.value[b] || b))).map(([key, items]) => ({ title: gameTypes.value[key] || items[0]?.game_type || key, games: items.slice(0, 8) }))
})
const money = computed(() => Number(profile.value?.balance ?? profile.value?.money ?? 0).toFixed(2))

/* ------------------------------------------------------------------------
 * 公告：逐条对齐旧版 web/10.735e88e52b03ea923563.js 里 app-home（模块 zpKS）
 * 的 separateNotice() / showNotice()，跑马灯 DOM 见同文件模块 oWfE 的子模板 O/k。
 *
 * 旧版 separateNotice()：
 *   notice_attribute 含 't' → noticeListT（顶部？模板里没用到）
 *   notice_attribute 含 'p' → noticeListP（进首页自动弹 alert）
 *   notice_attribute 含 'r' → noticeListR（跑马灯滚动，点击打开详情弹窗）
 *   noticeListP 会先 reverse()，再逐条判断 notice_id 是否已在
 *   sessionStorage['showAlertNoticeIds'] 里，没弹过就 appService.showAlert(标题, 内容)
 *   并把 id 记进去 —— 也就是「同一个会话里每条弹窗公告只弹一次」。
 *
 * 旧版 showAlert 的签名（从 main.js 里抽出来的原文）：
 *   showAlert(header, message, subHeader = "", buttons = ["确定"])
 *     → alertCtrl.create({ header, subHeader, message, buttons })
 *   ion-alert 的 message 走 innerHTML，所以公告正文是当富文本渲染的。
 *
 * 旧版 showNotice(notice)：
 *   modalCtrl.create({ component: app-notice, componentProps: { notice } }).present()
 *   （不是路由跳转。chunk 34 里那条 {path:"notice/:id"} 是没有入口的死路由。）
 * ---------------------------------------------------------------------- */
const noticeListR = ref([])
const noticeListT = ref([])
// 打开的公告详情弹窗（旧版的 app-notice modal）
const activeNotice = ref(null)
// p 类公告的待弹队列。旧版是连着 present() 多个 ion-alert 叠在一起，
// 用户实际也是关掉最上面一个才看到下一个，这里用队列等价实现。
const alertQueue = ref([])
const currentAlert = computed(() => alertQueue.value[0] || null)

/** 旧版 separateNotice() */
function separateNotice() {
  if (!notices.value.length) return
  const listR = []
  const listP = []
  const listT = []
  notices.value.forEach((item) => {
    // 后端 model 里 notice_attribute 的 type 是 array（存 JSON），接口返回的是数组，
    // 旧版直接 .indexOf('r')（数组的 indexOf）；这里两种形态都兼容一下
    const attribute = item?.notice_attribute
    const has = (flag) => (Array.isArray(attribute)
      ? attribute.indexOf(flag) >= 0
      : String(attribute || '').indexOf(flag) >= 0)
    if (has('t')) listT.push(item)
    if (has('p')) listP.push(item)
    if (has('r')) listR.push(item)
  })
  noticeListR.value = listR
  noticeListT.value = listT

  // 旧版 ionViewDidEnter 里先从 sessionStorage 读已弹过的 id
  let shown = []
  try { shown = JSON.parse(sessionStorage.getItem('showAlertNoticeIds') || '[]') } catch (_) { shown = [] }
  const pending = []
  // 旧版 this.noticeListP = this.noticeListP.reverse()
  listP.reverse().forEach((item) => {
    if (shown.indexOf(item.notice_id) === -1) {
      pending.push(item)
      shown.push(item.notice_id)
    }
    // 旧版这句在 forEach 里面（每轮都写一次），行为等价
    sessionStorage.setItem('showAlertNoticeIds', JSON.stringify(shown))
  })
  alertQueue.value = pending
}

/** 旧版 showNotice(notice)：打开 app-notice modal */
function showNotice(item) { activeNotice.value = item }

/** 弹窗公告点「确定」（或点背景）后关掉，露出队列里的下一条 */
function dismissAlert() { alertQueue.value = alertQueue.value.slice(1) }

function icon(game) {
  const key = String(game.game_id)
  return game.game_icon_url && !failed.value.has(key) ? game.game_icon_url : ''
}
function bad(game) { failed.value = new Set([...failed.value, String(game.game_id)]) }
function setTheme(value) { theme.value = value; localStorage.setItem('lottery-theme', value); themeOpen.value = false }
function closeMenus() { drawer.value = false; themeOpen.value = false }
function startEdgeSwipe(event) { swipeStartX.value = event.clientX <= 28 ? event.clientX : null }
function moveEdgeSwipe(event) { if (swipeStartX.value !== null && event.clientX - swipeStartX.value > 55) { drawer.value = true; swipeStartX.value = null } }
function endEdgeSwipe() { swipeStartX.value = null }
function setDisplayMode(mode) { displayMode.value = mode }
function listFrom(payload) {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  return payload.lists || payload.list || payload.rows || payload.items || payload.noticeList || payload.notice_lists || payload.notices || payload.data || []
}
const themeChoices = computed(() => theme.value === 'blue'
  ? [{ value: 'red', label: '红色', color: '#ff1720' }, { value: 'black', label: '黑色', color: '#000' }]
  : theme.value === 'black'
    ? [{ value: 'red', label: '红色', color: '#ff1720' }, { value: 'blue', label: '蓝色', color: '#101be8' }]
    : [{ value: 'blue', label: '蓝色', color: '#101be8' }, { value: 'black', label: '黑色', color: '#000' }])

onMounted(async () => {
  const [gamesResult, noticesResult, profileResult] = await Promise.allSettled([
    request('/game/lists'), request('/notice/lists', { method: 'POST', data: {}, form: true }), request('/user/profile')
  ])
  if (gamesResult.status === 'fulfilled') {
    gameTypes.value = gamesResult.value.data?.type || {}
    games.value = gamesResult.value.data?.lists || gamesResult.value.data || []
    const states = await Promise.allSettled(games.value.map((game) => request('/game/state', { data: { game: game.game_name || game.game_id } })))
    games.value = games.value.map((game, index) => {
      const state = states[index]?.status === 'fulfilled' ? states[index].value.data : null
      return state?.game_no ? { ...game, game_no: state.game_no, game_state: state } : game
    })
  } else {
    try {
      const publicGames = await request('/game/lists', { anonymous: true })
      games.value = publicGames.data?.lists || publicGames.data || []
    } catch { /* keep empty state */ }
  }
  if (noticesResult.status === 'fulfilled') {
    // 后端 Notice::lists() 直接返回 items() 数组；listFrom 兼容被包一层的情况
    notices.value = listFrom(noticesResult.value.data)
    // 旧版 getNoticeList() 成功后紧接着 separateNotice()
    separateNotice()
  }
  if (profileResult.status === 'fulfilled') profile.value = profileResult.value.data
})
</script>

<template>
  <main class="home" :class="`theme-${theme}`" @pointerdown="startEdgeSwipe" @pointermove="moveEdgeSwipe" @pointerup="endEdgeSwipe" @pointercancel="endEdgeSwipe">
    <header class="main-header">
      <button class="header-button" @click="drawer = true"><img src="/legacy/svg/ios-menu.svg" alt="菜单"></button>
      <b>彩票大厅</b>
      <button class="header-button" @click="themeOpen = !themeOpen"><img src="/legacy/svg/theme.svg" alt="风格"></button>
      <div v-if="themeOpen" class="theme-menu">
        <button v-for="choice in themeChoices" :key="choice.value" @click="setTheme(choice.value)"><i class="dot" :style="{ backgroundColor: choice.color }"></i>{{ choice.label }}</button>
      </div>
    </header>

    <!-- 旧版 O：*ngIf="noticeListR.length > 0" 才有这一条
         div.notice-list
           div.notice-list-icon    > ion-icon[name=volume-high] + span「公告：」
           div.notice-list-content > <marquee> > *ngFor noticeListR
                                                   <a (click)="showNotice(n)">{{notice_title}}</a>
         这里外壳沿用已经对好的 .notice 样式，内容按旧版改成「多条公告标题、可点击」。
         图标换成 md-volume-high.svg（旧版 md 模式下 name="volume-high" 取的就是这个）。 -->
    <div v-if="noticeListR.length" class="notice">
      <img src="/legacy/svg/md-volume-high.svg" alt="">
      <span>公告：</span>
      <em>
        <span>
          <a
            v-for="(item, index) in noticeListR"
            :key="item.notice_id ?? index"
            href="#"
            @click.prevent="showNotice(item)"
          >{{ item.notice_title }}</a>
        </span>
      </em>
    </div>
    <div class="filter"><img src="/legacy/svg/ios-list-box.svg" alt=""><span>显示：</span><button type="button" :class="{selected: displayMode === 'default'}" @click="setDisplayMode('default')">默认</button><button type="button" class="type" :class="{selected: displayMode === 'type'}" @click="setDisplayMode('type')">类型</button></div>
    <section v-for="section in gameSections" :key="section.title" class="game-section">
      <h2>{{ displayGameName(section.title) }}</h2>
      <div class="grid">
        <RouterLink v-for="game in section.games" :key="`${section.title}-${game.game_id}`" :to="`/game/${game.game_id}/bet`">
          <img v-if="icon(game)" :src="icon(game)" @error="bad(game)" alt="">
          <i v-else>彩票</i>
          <strong>{{ game.game_title || game.game_name || '彩票游戏' }}</strong>
          <small v-if="displayMode === 'default'" class="game-type">{{ displayGameName(gameTypeName(game)) }}</small>
          <small class="game-no">{{ game.game_no || game.period || game.current_period || game.game_id }}</small>
        </RouterLink>
      </div>
    </section>

    <!-- 底部导航统一用共享组件（对齐旧版 app-tabs 的 ion-tab-bar）。
         原来这里内联的那份用的是 ios-*.svg 图标、且「推广」错指到了 /user/service。 -->
    <AppBottomNav />

    <Transition name="drawer">
    <div v-if="drawer" class="drawer-layer" @click.self="closeMenus">
      <aside class="drawer">
        <header><button @click="drawer = false">‹</button><b>个人中心</b><RouterLink to="/user/profile">♟</RouterLink></header>
        <section class="drawer-identity"><img src="/legacy/images/userface-default.png" alt=""><div><strong>{{ profile?.nickname || profile?.username || '用户' }}</strong><small>账号：{{ profile?.username || '-' }}</small></div></section>
        <div class="drawer-total">账户余额：<b>￥ {{ money }}</b></div>
        <div class="drawer-balance"><span>可用：{{ money }}</span><span>冻结：{{ Number(profile?.frozen || 0).toFixed(2) }}</span></div>
        <div class="drawer-money"><RouterLink to="/user/money/1">充值</RouterLink><RouterLink to="/user/money/2">提现</RouterLink></div>
        <div class="drawer-links">
          <!-- 图标改回旧版那套彩色 iconfont symbol（导出自 web/assets/fonts/iconfont/iconfont.js） -->
          <RouterLink to="/user/service"><img src="/legacy/svg/iconfont/icon-kefu.svg" alt="">联系客服 <i>›</i></RouterLink>
          <RouterLink to="/user/password"><img src="/legacy/svg/iconfont/icon-password.svg" alt="">修改密码 <i>›</i></RouterLink>
          <RouterLink to="/user/bank"><img src="/legacy/svg/iconfont/icon-bank-card.svg" alt="">收款账号 <i>›</i></RouterLink>
          <RouterLink to="/user/finance"><img src="/legacy/svg/iconfont/icon-money-log.svg" alt="">交易明细 <i>›</i></RouterLink>
          <RouterLink to="/user/recharge"><img src="/legacy/svg/iconfont/icon-recharge.svg" alt="">充值记录 <i>›</i></RouterLink>
          <RouterLink to="/user/cash"><img src="/legacy/svg/iconfont/icon-cash.svg" alt="">提现记录 <i>›</i></RouterLink>
          <!-- 旧版 /game（app-game）是个只有顶栏「游戏中心」的空壳页，实际直达 /tabs/home，
               所以这里跟个人中心保持一致指向 /home -->
          <RouterLink to="/home"><img src="/legacy/svg/iconfont/icon-game-home.svg" alt="">游戏大厅 <i>›</i></RouterLink>
          <RouterLink to="/user/bets"><img src="/legacy/svg/iconfont/icon-bet-log.svg" alt="">游戏记录 <i>›</i></RouterLink>
          <!-- 「游戏账单」原来错指到了游戏记录页；旧版它是 chunk 23 的 app-bill，路由 /game/bill -->
          <RouterLink to="/game/bill"><img src="/legacy/svg/iconfont/icon-game-bill.svg" alt="">游戏账单 <i>›</i></RouterLink>
          <RouterLink to="/logout"><img src="/legacy/svg/iconfont/icon-logout.svg" alt="">安全退出 <i>›</i></RouterLink>
        </div>
      </aside>
    </div>
    </Transition>

    <Teleport to="body">
      <!-- 旧版 showNotice() 打开的 app-notice modal -->
      <NoticeModal v-if="activeNotice" :notice="activeNotice" @close="activeNotice = null" />

      <!-- 旧版 p 类公告进页面自动弹的 ion-alert：
           appService.showAlert(notice_title, notice_content) → buttons 默认 ["确定"] -->
      <LegacyAlert
        v-if="currentAlert"
        :header="currentAlert.notice_title"
        :message="currentAlert.notice_content"
        :buttons="['确定']"
        @confirm="dismissAlert"
        @cancel="dismissAlert"
      />
    </Teleport>
  </main>
</template>

<style scoped>
.main-header{box-shadow:0 2px 4px #0003}
</style>

<style scoped>
.home{--accent:#ff545b;min-height:100vh;padding-bottom:56px;background:#f5f5f5}.main-header{position:relative;z-index:3;display:flex;height:56px;align-items:center;justify-content:space-between;padding:0 16px;background:var(--accent);box-shadow:0 2px 4px #0003;color:#fff}.main-header>b{font-size:19px}.header-button{display:grid;place-items:center;border:0;background:none;transform:translateX(4px)}.header-button img{width:20px;filter:brightness(0) invert(1)}.theme-menu{position:absolute;top:44px;right:6px;min-width:82px;padding:4px 6px;background:#fff;box-shadow:0 2px 7px #0004;color:#222}.theme-menu button{display:flex;gap:7px;width:100%;padding:5px;border:0;background:#fff;text-align:left}.dot{width:13px;height:13px;border-radius:50%}.theme-blue{--accent:#4285f4}.theme-black{--accent:#222}.notice,.filter{display:flex;align-items:center;gap:6px;height:39px;padding:0 15px;background:#fff;border-bottom:7px solid #f4f4f4;font-size:13px}.notice img,.filter img{width:16px;filter:invert(42%) sepia(90%) saturate(2900%) hue-rotate(328deg)}.notice em{overflow:hidden;color:#aaa;font-style:normal;white-space:nowrap;text-overflow:ellipsis}.filter b{color:var(--accent);font-weight:400}.filter .type{color:#222}.game-section{margin-bottom:7px;padding-bottom:13px;background:#fff}.game-section h2{margin:0 14px 12px;padding:13px 0 0 8px;border-left:3px solid var(--accent);font-size:14px}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;padding:0 14px}.grid a{display:grid;justify-items:center;gap:4px;color:#444;text-align:center;text-decoration:none;font-size:12px}.grid a>img,.grid i{display:grid;width:66px;height:66px;place-items:center;border-radius:50%;background:#e54d79;color:#fff;object-fit:cover;font-size:20px;font-style:normal}.grid strong{font-weight:500}.grid small{font-size:10px;color:#999}.bottom-nav{position:fixed;bottom:0;left:0;right:0;z-index:2;display:grid;grid-template-columns:repeat(4,1fr);height:62px;background:#fff;border-top:1px solid #ddd}.bottom-nav a{display:grid;place-items:center;color:#777;text-decoration:none}.bottom-nav a.active{color:var(--accent)}.bottom-nav img{width:21px;opacity:.7;filter:grayscale(1) brightness(.55)}.bottom-nav a.active img{filter:var(--icon-filter)}.bottom-nav small{font-size:11px}.drawer-layer{position:fixed;inset:0;z-index:10;background:#0005}.drawer{width:min(76vw,380px);height:100%;overflow:auto;background:#f4f4f4;box-shadow:2px 0 5px #0004}.drawer>header{display:flex;height:52px;align-items:center;justify-content:space-between;padding:0 15px;background:var(--accent);color:#fff;box-shadow:0 2px 4px #0003}.drawer>header button,.drawer>header a{border:0;background:none;color:#fff;font-size:28px;text-decoration:none}.drawer>header b{font-size:19px}.drawer-identity{display:flex;align-items:center;gap:12px;padding:16px 13px;background:#fff}.drawer-identity img{width:56px;height:56px;border-radius:50%}.drawer-identity div{display:grid;gap:5px}.drawer-identity strong{font-size:17px}.drawer-identity small{color:#aaa}.drawer-total,.drawer-balance,.drawer-money{display:grid;background:#fff;text-align:center}.drawer-total{padding:14px;border-top:8px solid #f4f4f4}.drawer-balance,.drawer-money{grid-template-columns:1fr 1fr;border-top:1px solid #ddd}.drawer-balance span,.drawer-money a{padding:12px;border-right:1px solid #ddd;color:#333;text-decoration:none}.drawer-balance span:last-child,.drawer-money a:last-child{border-right:0}.drawer-links{margin-top:8px}.drawer-links a{display:flex;height:47px;align-items:center;gap:10px;padding:0 13px;border-bottom:1px solid #ddd;background:#fff;color:#333;text-decoration:none}.drawer-links a:nth-child(3),.drawer-links a:nth-child(7),.drawer-links a:nth-child(10){margin-top:7px}.drawer-links img{width:19px;height:19px}.drawer-links a i{margin-left:auto;color:#bbb;font-size:24px;font-style:normal}.theme-red,.theme-blue,.theme-black{--icon-filter:invert(42%) sepia(90%) saturate(2900%) hue-rotate(328deg)}.theme-blue{--icon-filter:invert(47%) sepia(89%) saturate(2200%) hue-rotate(198deg)}.theme-black{--icon-filter:brightness(0)}
 .home{--ion-tab-bar-color-activated:var(--accent)}.theme-menu{top:52px;right:4px;min-width:72px;padding:3px 4px;font-size:13px}.theme-menu button{gap:6px;padding:3px 4px;font-size:13px}.dot{width:12px;height:12px;flex:0 0 12px}.theme-blue .notice img,.theme-blue .filter img{filter:invert(47%) sepia(89%) saturate(2200%) hue-rotate(198deg)}.theme-black .notice img,.theme-black .filter img{filter:brightness(0)}
</style>
<style scoped>
.notice{overflow:hidden}
.notice em{display:block;flex:1;min-width:0;overflow:hidden;text-overflow:clip}
.notice em span{display:inline-block;min-width:max-content;padding-left:100%;animation:notice-marquee 18s linear infinite}
@keyframes notice-marquee{from{transform:translateX(0)}to{transform:translateX(-100%)}}
/* 旧版 .notice-list .notice-list-content a{color:#bbb;text-decoration:none;display:inline-block;margin-right:1rem} */
.notice em a{display:inline-block;margin-right:1rem;color:#bbb;text-decoration:none;cursor:pointer}
.filter button{padding:0;border:0;background:none;color:#222;font-size:13px;cursor:pointer}.filter button.selected{color:var(--accent)}
.game-section h2{margin:0 0 12px;padding:13px 14px 12px 22px;border-bottom:0 solid #ddd}.grid a>img,.grid i{width:20vw;height:20vw;max-width:96px;max-height:96px}
.game-section h2{position:relative;margin:0 0 12px;padding:13px 0 12px 22px;border-left:0;background:linear-gradient(#e8e8e8,#e8e8e8) left bottom/100% 1px no-repeat}.game-section h2::before{content:"";position:absolute;left:16px;top:13px;width:3px;height:16px;background:var(--accent)}
.grid strong{font-size:.8rem}.grid small{font-size:.8rem;color:#555}.grid small.game-no{display:block;font-size:.7rem;color:#666}
.drawer-enter-active,.drawer-leave-active{transition:opacity .24s ease}.drawer-enter-active .drawer,.drawer-leave-active .drawer{transition:transform .28s cubic-bezier(.22,.61,.36,1)}.drawer-enter-from,.drawer-leave-to{opacity:0}.drawer-enter-from .drawer,.drawer-leave-to .drawer{transform:translateX(-100%)}
.drawer{width:min(60vw,320px)}
.drawer-total{display:flex;align-items:center;justify-content:center;gap:8px;padding:14px 8px;white-space:nowrap;font-size:14px}.drawer-total b{font-size:14px}.drawer-balance,.drawer-money{font-size:14px}.drawer-links a{font-size:14px}
</style>
